import React, { ReactElement, ReactNode, Ref } from 'react';

import { Column } from '@tanstack/react-table';

import Checkbox from '@adsk/alloy-react-checkbox';
import { sva } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';

export type ColumnOptionProps<D extends Record<string, unknown>> = {
  children?: ReactNode;
  column: Column<D, unknown>;
  disabled?: boolean;
  getColumnName?: (column: Column<D, unknown>) => React.ReactNode;
  style?: React.CSSProperties;
};

const defaultColumnName = <D extends Record<string, unknown>>(
  column: Column<D, unknown>,
) => column.id;

const ColumnOption = <D extends Record<string, unknown>>(
  {
    column,
    children,
    disabled,
    getColumnName = defaultColumnName,
    style = {},
    ...restProps
  }: ColumnOptionProps<D>,
  ref: Ref<HTMLLabelElement>,
) => {
  const theme = useTheme();
  const option = theme.tokens?.component?.table?.configuration?.option;

  const optionTokens = sva(option?._, {
    variants: {
      children: option?.children,
      disabled: option?.disabled,
    },
  });

  return (
    <label
      ref={ref}
      css={[optionTokens({ children: !!children, disabled }), { ...style }]}
      {...restProps}
    >
      {children}
      {column.getCanHide() && (
        <Checkbox
          checked={column.getIsVisible()}
          disabled={disabled}
          onChange={(checked) => column.toggleVisibility(!!checked)}
          style={{ marginRight: 12 }}
        />
      )}
      <span dir="auto">{getColumnName(column)}</span>
    </label>
  );
};

export default React.forwardRef(ColumnOption) as <
  D extends Record<string, unknown>,
>(
  p: ColumnOptionProps<D> & { ref?: Ref<HTMLLabelElement> },
) => ReactElement;
